'use client'

import dynamic from 'next/dynamic'
import { type FC, useEffect, useMemo } from 'react'
import { useHits, useInstantSearch } from 'react-instantsearch'
import { FormattedMessage } from 'react-intl'

import { apmCaptureError } from '../../../../apm'
import { CustomError } from '../../../../base-errors'
import { ErrorBoundary } from '../../../../error-boundary'

import { SearchPageNoResultContactForm } from './SearchPageNoResultContactForm'
import { SearchPageNoResultNewSearchForm } from './SearchPageNoResultNewSearchForm'

const SearchPageNoResultsCrossSellSliderWidget = dynamic(async () =>
  import('../../SearchPageNoResultsCrossSellSliderWidget').then(
    (mod) => mod.SearchPageNoResultsCrossSellSliderWidget,
  ),
)

const shouldHideNoResults = ({
  filtersApplied,
  isArtificial,
  nbHits,
  status,
}: {
  filtersApplied: boolean
  isArtificial: boolean
  nbHits: number
  status: string
}): boolean => {
  if (status === 'error') {
    return false
  }

  // The `__isArtificial` flag makes sure not to display the No Results message when no hits have been returned.
  // https://www.algolia.com/doc/guides/building-search-ui/going-further/conditional-display/react/#handling-no-results
  return isArtificial || filtersApplied || nbHits > 0 || status !== 'idle'
}

const useCaptureError = ({
  error,
  status,
}: {
  error: unknown
  status: string
}): void => {
  useEffect(() => {
    if (status === 'error') {
      const customError = new CustomError(
        {
          message:
            error instanceof Error
              ? error.message
              : 'Unknown Instant Search error',
          name: 'SearchPageAlgoliaError',
        },
        {
          cause: error,
        },
      )

      apmCaptureError(customError)
    }
  }, [status, error])
}

const hasActiveFilters = ({
  range = {},
  refinementList = {},
}: {
  range?: Record<string, string>
  refinementList?: Record<string, string[]>
}): boolean => {
  const hasRanges = Object.values(range).some((entry) =>
    Object.values(entry).some((item) => item.length > 0),
  )

  const hasRefinementLists = Object.values(refinementList).some(
    (items) => items.length > 0,
  )

  return hasRanges || hasRefinementLists
}

export const SearchPageNoResult: FC = () => {
  const { error, indexUiState, status } = useInstantSearch({
    catchError: true,
  })

  useCaptureError({ error, status })

  /**
   * Use `results` from `useHits` instead of `useInstantSearch` to have a consistent
   * `__isArtificial` value on client and server sides. If a search query has empty results,
   * these values are different. Example http://localhost:3000/search.htm?i=1&q=4025877614422:
   *
   * - server: __isArtificial = true,
   * - client: __isArtificial = false
   */
  const { results } = useHits()

  const { range, refinementList } = indexUiState

  const filtersApplied = useMemo(
    () =>
      hasActiveFilters({
        range,
        refinementList,
      }),
    [range, refinementList],
  )

  const { __isArtificial: isArtificial = false, nbHits = 0 } = results ?? {}

  if (shouldHideNoResults({ filtersApplied, isArtificial, nbHits, status })) {
    return null
  }

  return (
    <div className="flex flex-col gap-3 desktop:items-start desktop:gap-6">
      <h1 className="text-2xl font-medium tablet:text-4xl">
        <FormattedMessage id="searchResults.empty.title" />
      </h1>

      <div className="grid gap-6 tablet:grid-cols-3">
        <SearchPageNoResultNewSearchForm className="tablet:col-span-2" />

        <SearchPageNoResultContactForm />
      </div>

      <ErrorBoundary>
        <SearchPageNoResultsCrossSellSliderWidget
          className="w-full"
          itemsPerPage={6}
        />
      </ErrorBoundary>
    </div>
  )
}
